'use client'

import {
  trackSurveyImpression,
  trackSurveyResponse,
  trackVideoSurveyImpression,
  trackVideoSurveyResponse,
} from '@/lib/survey'
import { loadAnsweredQuestionIds, markQuestionAnswered } from '@/lib/surveyAnswered'
import type { SurveyAnswer, SurveyQuestion } from '@/types/interactiveVideo'
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSurveySheet, type SurveySheetState } from '@/components/embedding/survey/useSurveySheet'
import { isEndCued } from '@/components/embedding/survey/surveyCue'

export interface UseModalSurveyParams {
  /** 現在再生中ノードの設問（提示タイミング付き・プレイヤー型へ変換済み）。呼び出し側で memo すること */
  questions: SurveyQuestion[]
  /** 現在ノードのキー（回答保存 API とノード切替時の状態リセットに使う） */
  nodeKey: string
  /** 中央の動画要素 ref（PC/SP 共通） */
  videoRef: RefObject<HTMLVideoElement | null>
  /**
   * 回答の保存先。埋め込みは `{ kind: 'embed', scenarioId }`、動画詳細ページは
   * `{ kind: 'videoDetail', videoId, scenarioId? }`（ADR-0015）。未指定なら送信しない（プレビュー等）。
   */
  target?: SurveyTarget
  /**
   * 指定すると、末尾（null-cue）の設問を動画終了ではなく「終了の N 秒前」に提示する。
   * WIDE型の分岐ノードで、選択肢（終了直前から表示）より先に末尾アンケートへ答えさせるために使う
   * （選択肢を再生後に初めて出さないため・branchChoiceTiming.ts）。未指定なら従来どおり終了時。
   */
  endCueLeadSec?: number
  /** プレイヤーが開いていて視聴中か（false のあいだは cue 監視をしない） */
  active: boolean
}

/** 回答の復元が済むまで cue 判定へ渡す空配列（毎回作ると参照が変わって再計算を誘発する） */
const EMPTY_QUESTIONS: SurveyQuestion[] = []

/** アンケート回答の保存先（発生元によって叩く公開APIが変わる・ADR-0015）。 */
export type SurveyTarget =
  | { kind: 'embed'; scenarioId: string }
  | { kind: 'videoDetail'; videoId: string; scenarioId?: string | null }

export interface UseModalSurveyResult {
  /** アンケートシートの状態（PC=右パネル / SP=下シートに描画する） */
  survey: SurveySheetState
  /** 末尾(null-cue)で未回答の設問数。動画評価画面より先にアンケートを出すための判定に使う */
  pendingEndCount: number
}

/**
 * 動画紐づきアンケートの視聴側状態フック（#244）。
 *
 * #238（Pattern D）の cue 同期・回答保存ロジック（surveyCue / useSurveySheet /
 * trackSurveyResponse）をそのまま再利用し、動画要素の currentTime/ended を購読して
 * 提示判定に渡す。回答済み questionId は視聴セッション単位で保持しノードをまたいで
 * 再提示しない。描画位置は呼び出し側が担う（SLIDER/TILE の VideoModal＝PC右パネル/SP下シート、
 * POPUP の InteractiveVideoPlayer＝下シートオーバーレイ・ADR-0014）。
 */
export function useModalSurvey({
  questions,
  nodeKey,
  videoRef,
  target,
  endCueLeadSec,
  active,
}: UseModalSurveyParams): UseModalSurveyResult {
  const [currentTime, setCurrentTime] = useState(0)
  const [videoEnded, setVideoEnded] = useState(false)
  // 動画長。cueTime が動画長を超える設問を末尾提示へ倒すのに使う（ADR-0015）
  const [videoDuration, setVideoDuration] = useState<number | null>(null)
  // 回答済み questionId（視聴セッション単位・ノードをまたいで保持し再提示を防ぐ）。
  // 再読み込みで答え直しにならないよう端末にも残す（`surveyAnswered`・30分の視聴セッション単位）。
  const [answeredIds, setAnsweredIds] = useState<Set<string>>(() => new Set())
  // 読み出しは localStorage なので client でしかできない。復元が済むまでは提示判定を
  // 止めておく（済んだ設問が一瞬出てから消えるのを防ぐ）。
  const [answersRestored, setAnswersRestored] = useState(false)
  useEffect(() => {
    setAnsweredIds(loadAnsweredQuestionIds())
    setAnswersRestored(true)
  }, [])

  // 復元前は「設問なし」として扱い、ゲートも cue 判定も動かさない
  const cueQuestions = useMemo(
    () => (answersRestored ? questions : EMPTY_QUESTIONS),
    [answersRestored, questions],
  )

  // 動画要素の再生位置・末尾到達を購読（TelopDisplay と同じ currentTime 監視方式）。
  // ノード切替（nodeKey 変化）で貼り直し、状態をリセットする。
  useEffect(() => {
    const video = videoRef.current
    if (!active || !video) return
    setCurrentTime(video.currentTime || 0)
    setVideoEnded(video.ended)
    setVideoDuration(Number.isFinite(video.duration) ? video.duration : null)
    const onTime = () => setCurrentTime(video.currentTime)
    const onEnded = () => setVideoEnded(true)
    const onPlay = () => setVideoEnded(false)
    const onMeta = () =>
      setVideoDuration(Number.isFinite(video.duration) ? video.duration : null)
    video.addEventListener('timeupdate', onTime)
    video.addEventListener('ended', onEnded)
    video.addEventListener('play', onPlay)
    video.addEventListener('loadedmetadata', onMeta)
    return () => {
      video.removeEventListener('timeupdate', onTime)
      video.removeEventListener('ended', onEnded)
      video.removeEventListener('play', onPlay)
      video.removeEventListener('loadedmetadata', onMeta)
    }
  }, [videoRef, active, nodeKey])

  // 必須×最初（cueTime===0）の視聴前ゲート（ADR-0013）。
  // useSurveySheet の pickForcedQuestion がシート展開＋pauseVideo する一方、autoplay は
  // 数フレーム再生してしまうため、未回答の間は 'play' を捕まえて先頭へ戻し「一瞬の再生」も防ぐ。
  // ゲートが解けた（回答済み）ら保留していた再生を開始する。
  const startGatePending = useMemo(
    () => cueQuestions.some((q) => q.cueTime === 0 && q.required && !answeredIds.has(q.id)),
    [cueQuestions, answeredIds],
  )

  const wasStartGatePendingRef = useRef(false)
  useEffect(() => {
    const video = videoRef.current
    if (!active || !video) return
    if (startGatePending) {
      wasStartGatePendingRef.current = true
      const hold = () => {
        video.pause()
        if (video.currentTime > 0) video.currentTime = 0
      }
      hold() // 既に autoplay が始まっていても即座に先頭で止める
      video.addEventListener('play', hold)
      return () => video.removeEventListener('play', hold)
    }
    if (wasStartGatePendingRef.current) {
      // 保留していた再生を開始（resumeVideo が hold に捕まっても、ここで確実に再生する）
      wasStartGatePendingRef.current = false
      if (!video.ended) video.play().catch(() => {})
    }
  }, [videoRef, active, startGatePending, nodeKey])

  const pauseVideo = useCallback(() => {
    videoRef.current?.pause()
  }, [videoRef])

  const resumeVideo = useCallback(() => {
    const video = videoRef.current
    if (video && !video.ended) {
      video.play().catch(() => {})
    }
  }, [videoRef])

  const handleAnswer = useCallback(
    (question: SurveyQuestion, answer: SurveyAnswer) => {
      setAnsweredIds((prev) => {
        const next = new Set(prev)
        next.add(question.id)
        return next
      })
      // 再読み込みしても答え直しにならないよう残す
      markQuestionAnswered(question.id)
      if (target?.kind === 'embed') {
        trackSurveyResponse({
          scenarioId: target.scenarioId,
          nodeKey,
          questionId: question.id,
          optionIds: answer.optionIds,
          freeText: answer.freeText,
          leadValue: answer.leadValue ?? null,
        })
      } else if (target?.kind === 'videoDetail') {
        trackVideoSurveyResponse({
          videoId: target.videoId,
          scenarioId: target.scenarioId ?? null,
          questionId: question.id,
          optionIds: answer.optionIds,
          freeText: answer.freeText,
          leadValue: answer.leadValue ?? null,
        })
      }
    },
    [target, nodeKey],
  )

  // 提示到達（到達→完了ファネルの土台・ADR-0015）。設問があり視聴が始まったら1回だけ記録する
  // （サーバ側でも視聴セッション×テンプレ×紐づけ先で重複排除するので二重送信は無害）。
  const impressionSentRef = useRef<string | null>(null)
  useEffect(() => {
    if (!active || questions.length === 0 || !target) return
    const key = `${nodeKey}:${questions[0]?.id ?? ''}`
    if (impressionSentRef.current === key) return
    impressionSentRef.current = key
    if (target.kind === 'embed') {
      trackSurveyImpression({ scenarioId: target.scenarioId, nodeKey })
    } else {
      trackVideoSurveyImpression({
        videoId: target.videoId,
        scenarioId: target.scenarioId ?? null,
      })
    }
  }, [active, questions, nodeKey, target])

  // 末尾設問を提示してよい状態か。endCueLeadSec 指定時は終了の N 秒前から末尾扱いにする
  // （必須なら一時停止して回答 → 再開で残りを再生する）。未指定なら実際の終了時のみ。
  const endCueReached =
    videoEnded ||
    (endCueLeadSec !== undefined &&
      videoDuration !== null &&
      videoDuration > 0 &&
      currentTime >= videoDuration - endCueLeadSec)

  const survey = useSurveySheet({
    questions: cueQuestions,
    nodeKey,
    currentTime,
    videoEnded: endCueReached,
    answeredIds,
    videoDuration,
    onAnswer: handleAnswer,
    pauseVideo,
    resumeVideo,
  })

  // 末尾提示で「終了処理をブロックすべき」未回答設問数。必須(required)のみ数える。
  // 任意設問は回答せず閉じられ（onCloseSheet は回答済みにしない）、数え続けると
  // pendingEndCount が 0 に戻らず動画評価/次動画へ進めなくなるため除外する。
  const pendingEndCount = useMemo(
    () =>
      cueQuestions.filter(
        (q) => isEndCued(q, videoDuration) && q.required && !answeredIds.has(q.id),
      ).length,
    [cueQuestions, answeredIds, videoDuration],
  )

  // ノード切替時に末尾状態をリセット（次動画の頭出しで videoEnded を持ち越さない）
  const prevNodeKeyRef = useRef(nodeKey)
  useEffect(() => {
    if (prevNodeKeyRef.current !== nodeKey) {
      prevNodeKeyRef.current = nodeKey
      setVideoEnded(false)
      setCurrentTime(0)
    }
  }, [nodeKey])

  return { survey, pendingEndCount }
}
